import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Api } from '../../services/api';
import { MatCardModule } from '@angular/material/card';
import { HttpHeaders } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatGridListModule } from '@angular/material/grid-list';
import { StateService } from '../../services/state';
import { spanishPlateValidator } from '../../validators/spanish-plate.validator';
import { postalEspanolSenseCeutaMelillaValidator } from '../../validators/spanish-postal-code.validator';
import { minAgeValidator } from '../../validators/min-age.validator';
import { firstValueFrom } from 'rxjs';
import { Notifications } from '../../services/notifications';
import { version } from 'moment';
import { vehicleBrandValidator } from '../../validators/vehicle-brand.validator';

export const AUTOCARAVANA_TYPE = '444';

export interface Brand {
  codigo: string;
  nombre: string;
}

export interface Model {
  codigo: string;
  nombre: string;
}

export interface Version {
  codigo: string;
  nombre: string;
}

@Component({
  selector: 'app-price-form',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatCardModule,
    MatIconModule,
    MatDatepickerModule,
    MatGridListModule,
  ],
  templateUrl: './price-form.html',
  styleUrl: './price-form.scss',
})
export class PriceForm {
  priceForm!: FormGroup;
  filteredModels: Model[] = [];
  models: Model[] = [];
  filteredBrands: Brand[] = [];
  brands: Brand[] = [];
  versions: Version[] = [];
  filteredVersions: Version[] = [];
  selectedBrand: Brand | null = null;
  selectedModel: Model | null = null;
  selectedVersion: Version | null = null;
  type = 1;
  state: any;

  private readonly api: Api = inject(Api);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly stateService = inject(StateService);
  private readonly notifications = inject(Notifications);

  minAgeByVehicle = (type: any) => {
    return parseInt(type) === 3 ? 18 : 24;
  };

  async ngOnInit() {
    this.state = this.stateService.getState();

    this.priceForm = this.formBuilder.group(
      {
        vehicleType: [this.state?.vehicleType, Validators.required],
        brandName: [
          { value: this.state?.brandName, disabled: true },
          [Validators.required, vehicleBrandValidator()],
        ],
        modelName: [{ value: this.state?.modelName, disabled: true }, Validators.required],
        versionName: [{ value: this.state?.versionName, disabled: true }, Validators.required],
        licensePlate: [this.state?.licensePlate, [Validators.required, spanishPlateValidator()]],
        birthday: [this.state?.tomador?.birthday, Validators.required],
        postalCode: [
          this.state?.postalCode,
          [Validators.required, postalEspanolSenseCeutaMelillaValidator()],
        ],
        vehicleCategory: [this.state?.vehicleCategory],
      },
      {
        validators: [minAgeValidator('vehicleType', 'birthday', this.minAgeByVehicle)],
      }
    );

    // Enable/disable brand, model and version controls based on vehicle type selection
    const vehicleTypeCtrl = this.priceForm.get('vehicleType');
    const brandCtrl = this.priceForm.get('brandName');
    const modelCtrl = this.priceForm.get('modelName');
    const versionCtrl = this.priceForm.get('versionName');

    vehicleTypeCtrl!.valueChanges.subscribe((value) => {
      if (value) {
        brandCtrl!.enable();
        modelCtrl!.enable();
        versionCtrl!.enable();
      } else {
        brandCtrl!.reset();
        modelCtrl!.reset();
        versionCtrl!.reset();

        brandCtrl!.disable();
        modelCtrl!.disable();
        versionCtrl!.disable();
      }
    });

    // If there's a saved state, pre-fill the form
    if (this.state?.vehicleType) {
      this.type = this.state.vehicleType;
      brandCtrl!.enable();
      modelCtrl!.enable();
      versionCtrl!.enable();

      this.brands = await firstValueFrom(
        this.api.post(
          '/seguro-dias/stored/brands',
          { category: this.state.vehicleType /*, type: this.type*/ },
          { headers: new HttpHeaders().set('Content-Type', 'application/json') }
        )
      );
      this.brands.sort((a, b) => a.nombre.localeCompare(b.nombre));

      await this.selectBrand(this.state.brandName, false);
      if (this.state.modelName) {
        await this.selectModel(this.state.modelName);
      }
      if (this.state.versionName) {
        this.selectVersion(this.state.versionName);
      }
    }

    this.priceForm.get('vehicleType')?.valueChanges.subscribe((value) => {
      if (this.type != value) {
        this.resetBrandModelVersion(value);
      }
    });

    this.route.params.subscribe((params) => {
      this.type = this.paramToType(params['type']);
    });
  }

  resetBrandModelVersion(value: number) {
    this.type = value;
    this.versions = [];
    this.filteredVersions = [];
    this.models = [];
    this.filteredModels = [];
    this.brands = [];
    this.filteredBrands = [];
    this.priceForm.patchValue({ brandName: '', modelName: '', versionName: '' });
    if (value) {
      this.api
        .post(
          '/seguro-dias/stored/brands',
          { category: value /*, type: this.type*/ },
          { headers: new HttpHeaders().set('Content-Type', 'application/json') }
        )
        .subscribe((data: any) => {
          let brands = data;
          if (brands && !Array.isArray(brands)) {
            brands = [brands];
          }
          brands.sort((a: Brand, b: Brand) => a.nombre.localeCompare(b.nombre));
          this.brands = this.filteredBrands = brands || [];
        });
    } else {
      this.brands = [];
      this.filteredBrands = [];
    }
  }

  paramToType(param: string): number {
    this.priceForm.patchValue({ vehicleCategory: param });
    switch (param) {
      case 'autocaravanas':
        // A Divina les autocaravanes van pel tipus de vehicle 444 (MDAU0407).
        // El valueChanges de vehicleType ja crida resetBrandModelVersion si
        // canvia el tipus; tornar-lo a cridar aquí duplicava la petició.
        this.priceForm.patchValue({ vehicleType: AUTOCARAVANA_TYPE });
        return +AUTOCARAVANA_TYPE;
      case 'campers':
        this.priceForm.patchValue({ vehicleType: '1' });
        this.resetBrandModelVersion(1);
        return 1;
      case 'modernos':
      case 'clasicos':
      default:
        return this.state?.vehicleType ? this.state.vehicleType : 0;
    }
  }
  onSubmit() {
    if (this.priceForm.valid) {
      const priceData = Object.assign(this.state, this.priceForm.value);

      let idBrandDivina = this.selectedBrand
        ? this.selectedBrand?.codigo
        : this.state?.idBrandDivina;

      if (!idBrandDivina) {
        this.notifications.showError('Seleccione una marca válida del listado.');
        return;
      }

      let idModelDivina = this.selectedModel
        ? this.selectedModel?.codigo
        : this.state?.idModelDivina;

      if (!idModelDivina) {
        this.notifications.showError('Seleccione un modelo válido del listado.');
        return;
      }

      let idVersionDivina = this.selectedVersion
        ? this.selectedVersion?.codigo
        : this.state?.idVersionDivina;

      if (!idVersionDivina) {
        this.notifications.showError('Seleccione una versión válida del listado.');
        return;
      }

      this.stateService.setState(
        Object.assign(priceData, {
          idBrandDivina: idBrandDivina,
          idModelDivina: idModelDivina,
          idVersionDivina: idVersionDivina,
          tomador: Object.assign({}, this.state?.tomador, { birthday: priceData.birthday }),
        })
      );

      this.api
        .post(
          '/seguro-dias',
          {
            campaignId: this.state?.campaignId ?? null,
            collaboratorId: this.state?.collaboratorId ?? null,
            plate: priceData.licensePlate,
            brand: priceData.brandName,
            idBrandDivina: this.selectedBrand?.codigo,
            model: priceData.modelName,
            idModelDivina: this.selectedModel?.codigo,
            version: priceData.versionName,
            idVersionDivina: this.selectedVersion?.codigo,
            type: this.type ? this.type : this.state?.vehicleType,
            postalCode: priceData.postalCode,
            birthday: priceData.birthday,
            tkn: this.state?.tkn,
            days: Math.min(this.state?.duration ?? 1, 30),
          },
          {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
          }
        )
        .subscribe(
          (data: any) => {
            this.stateService.setState(Object.assign(this.stateService.getState(), { ...data }));
            this.router.navigate(['/price-result']);
          },
          (error) => {
            //TODO notify error
          }
        );
    }
  }

  filterBrands(event: any) {
    this.selectedBrand = null;
    this.selectedModel = null;
    this.selectedVersion = null;
    this.models = this.filteredModels = [];
    this.versions = this.filteredVersions = [];
    this.priceForm.patchValue({ modelName: '', versionName: '' });
    this.priceForm.get('brandName')!.setErrors({ noOptionSelected: true });
    this.filteredBrands = this.brands.filter((brand) =>
      brand.nombre.toLowerCase().includes(event.target.value.toLowerCase())
    );
  }

  filterModels(event: any) {
    this.selectedModel = null;
    this.selectedVersion = null;
    this.versions = this.filteredVersions = [];
    this.priceForm.patchValue({ versionName: '' });
    this.priceForm.get('modelName')!.setErrors({ noOptionSelected: true });
    this.filteredModels = this.models.filter((model) =>
      model.nombre.toLowerCase().includes(event.target.value.toLowerCase())
    );
  }

  filterVersions(event: any) {
    this.selectedVersion = null;
    this.priceForm.get('versionName')!.setErrors({ noOptionSelected: true });
    this.filteredVersions = this.versions.filter((version) =>
      version.nombre.toLowerCase().includes(event.target.value.toLowerCase())
    );
  }

  async selectBrand(brandName: string, clearModel: boolean = true) {
    const brand = this.brands.find((b) => b.nombre === brandName);
    this.versions = this.filteredVersions = [];
    if (brand) {
      this.selectedBrand = brand;
      this.priceForm.get('brandName')!.updateValueAndValidity();
      let models = (await firstValueFrom(
        this.api.post(
          '/seguro-dias/stored/models',
          {
            category: this.type,
            brand: brand.codigo,
            //type: this.type,
          },
          { headers: new HttpHeaders().set('Content-Type', 'application/json') }
        )
      )) as Model[];

      if (models && !Array.isArray(models)) {
        models = [models];
      }
      models.sort((a, b) => a.nombre.localeCompare(b.nombre));
      this.models = this.filteredModels = models || [];
      if (clearModel) this.priceForm.patchValue({ modelName: '' });
      if (this.state.modelName) {
        this.selectModel(this.state.modelName);
      }
    } else {
      this.models = [];
      this.filteredModels = [];
      this.selectedBrand = null;
    }
    this.priceForm.patchValue({ modelName: '', versionName: '' });
  }

  onBrandSelected(event: MatAutocompleteSelectedEvent) {
    const brandName = event.option.value;
    this.selectBrand(brandName);
  }

  onModelSelected(event: MatAutocompleteSelectedEvent) {
    const modelName = event.option.value;
    this.selectModel(modelName);
  }

  onVersionSelected(event: MatAutocompleteSelectedEvent) {
    const versionName = event.option.value;
    this.selectVersion(versionName);
  }

  async selectModel(modelName: string, clearVersion: boolean = true) {
    const model = this.models.find((m) => m.nombre === modelName);
    if (model) {
      this.selectedModel = model;
      this.priceForm.get('modelName')!.updateValueAndValidity();
      this.priceForm.patchValue({ modelName: modelName });
      if (clearVersion) {
        this.priceForm.patchValue({ versionName: '' });
        this.selectedVersion = null;
      }

      let versions = (await firstValueFrom(
        this.api.post(
          '/seguro-dias/stored/versions',
          {
            brand: this.selectedBrand?.codigo,
            model: model.codigo,
            category: this.type,
          },
          { headers: new HttpHeaders().set('Content-Type', 'application/json') }
        )
      )) as Model[];

      if (versions && !Array.isArray(versions)) {
        versions = [versions];
      }
      versions.sort((a, b) => a.nombre.localeCompare(b.nombre));
      this.versions = this.filteredVersions = versions || [];
    } else {
      this.versions = this.filteredVersions = [];
      this.selectedModel = null;
    }
    this.priceForm.patchValue({ versionName: '' });
  }

  selectVersion(versionName: string) {
    const version = this.versions.find((v) => v.nombre === versionName);
    if (version) {
      this.selectedVersion = version;
      this.priceForm.get('versionName')!.updateValueAndValidity();
      this.priceForm.patchValue({ versionName: versionName });
    } else {
      this.selectedVersion = null;
    }
  }
}
